import React, { useContext, useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf/dist/esm/entry.webpack";
import { useHistory, useLocation, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Helmet } from "react-helmet";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MobileStepper from "@mui/material/MobileStepper";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DataUsageOutlinedIcon from "@mui/icons-material/DataUsageOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventNoteIcon from "@mui/icons-material/EventNote";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";

import { DatabaseContext, UserContext } from "../../../../context";
import firebase from "../../../../firebase";
import { deleteFileRecordAndNotifications } from "../fileDeletion";
import {
    doesFileHaveDuplicate,
    shouldFileBeVisible,
} from "../../../../utility";
import ToolbarWithLogo from "../../../ToolbarWithLogo";
import NoPermissionsMessage from "../../../NoPermissionsMessage";
import CommentSection from "../CommentSection";
import DeleteFileDialog from "../DeleteFileDialog";
import EditFileDialog from "../EditFileDialog";
import FileEventsDialog from "../FileEventsDialog";
import FileUsageDialog from "../FileUsageDialog";
import ShareLinkDialog from "../ShareLinkDialog";
import { useBlobFileDownload } from "../useFileDownloads";

export default function PDFViewerPage() {
    const history = useHistory();
    const location = useLocation();
    const { pdfKey } = useParams();
    const downloadFile = useBlobFileDownload();
    const pdfPageHostRef = useRef(null);
    const pdfPresentationRef = useRef(null);
    const loggedUsageKeyRef = useRef(null);

    const { files, comments, events, users, folders, groups } =
        useContext(DatabaseContext);
    const { currentUserData } = useContext(UserContext);

    const [numPages, setNumPages] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfWidth, setPdfWidth] = useState();

    const [folder, setFolder] = useState();
    const [file, setFile] = useState();
    const [uploadedBy, setUploadedBy] = useState("");

    const [userHasPermission, setUserHasPermission] = useState();
    const [userIsManager, setUserIsManager] = useState(false);
    const [isInFavourites, setIsInFavourites] = useState(false);

    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmDeleteDialogIsOpen, setConfirmDeleteDialogIsOpen] =
        useState(false);
    const [deleteInProgress, setDeleteInProgress] = useState(false);
    const [eventDialogOpen, setEventDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [usageDialogOpen, setUsageDialogOpen] = useState(false);
    const [shareLinkDialogOpen, setShareLinkDialogOpen] = useState(false);
    const [isPresentationMode, setIsPresentationMode] = useState(false);
    const [isPresentationFallback, setIsPresentationFallback] =
        useState(false);

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            if (!user) {
                history.replace("/signin?url=" + location.pathname);
            }
        });

        if (currentUserData) {
            const matchingFile = files.find(
                (candidate) => candidate.key === pdfKey,
            );

            if (matchingFile) {
                setFile(matchingFile);

                const matchingFolder = folders.find(
                    (candidate) => candidate.key === matchingFile.folder,
                );
                setFolder(matchingFolder);

                setUserHasPermission(
                    shouldFileBeVisible(
                        matchingFile,
                        groups,
                        currentUserData,
                    ),
                );

                setIsInFavourites(
                    Boolean(
                        currentUserData.faveFiles?.includes(matchingFile.key),
                    ),
                );

                setUserIsManager(
                    matchingFile.managers.includes(currentUserData.key) ||
                        currentUserData.role === "System Digital Manager" ||
                        matchingFile.uploadedBy === currentUserData.key,
                );

                const uploadedByUser = users.find(
                    (user) => user.key === matchingFile.uploadedBy,
                );
                setUploadedBy(
                    uploadedByUser
                        ? `${uploadedByUser.firstName} ${uploadedByUser.lastName}`
                        : "",
                );

                if (loggedUsageKeyRef.current !== matchingFile.key) {
                    firebase.database().ref("fileusage").push({
                        file: matchingFile.key,
                        fileName: matchingFile.displayName,
                        fileType: matchingFile.type,
                        user: currentUserData.key,
                        action: "Open",
                        date: dayjs().toString(),
                    });
                    loggedUsageKeyRef.current = matchingFile.key;
                }
            }
        }

        return () => unsubscribe();
    }, [
        currentUserData,
        files,
        folders,
        groups,
        history,
        location.pathname,
        pdfKey,
        users,
    ]);

    useEffect(() => {
        setPageNumber(1);
        setNumPages(0);
    }, [pdfKey]);

    useEffect(() => {
        const pageHost = pdfPageHostRef.current;
        if (!pageHost) {
            return undefined;
        }

        const updatePDFWidth = () => {
            const horizontalPadding = window.innerWidth < 600 ? 24 : 40;
            setPdfWidth(
                Math.max(260, pageHost.clientWidth - horizontalPadding),
            );
        };

        updatePDFWidth();
        window.addEventListener("resize", updatePDFWidth);

        const resizeObserver =
            typeof ResizeObserver !== "undefined"
                ? new ResizeObserver(updatePDFWidth)
                : null;
        resizeObserver?.observe(pageHost);

        return () => {
            window.removeEventListener("resize", updatePDFWidth);
            resizeObserver?.disconnect();
        };
    }, [file, numPages, userHasPermission]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            const isThisViewerFullscreen =
                document.fullscreenElement === pdfPresentationRef.current;

            setIsPresentationMode(isThisViewerFullscreen);
            setIsPresentationFallback(false);
        };

        document.addEventListener(
            "fullscreenchange",
            handleFullscreenChange,
        );

        return () =>
            document.removeEventListener(
                "fullscreenchange",
                handleFullscreenChange,
            );
    }, []);

    useEffect(() => {
        if (!isPresentationFallback) {
            return undefined;
        }

        const previousBodyOverflow = document.body.style.overflow;
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setIsPresentationMode(false);
                setIsPresentationFallback(false);
            }
        };

        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", handleEscape);

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isPresentationFallback]);

    function handleBackToFiles() {
        const returnTo = location.state?.returnTo;

        if (returnTo && returnTo.startsWith("/files")) {
            history.goBack();
            return;
        }

        if (file?.folder) {
            history.push(`/files/${file.folder}`);
        } else {
            history.push("/files");
        }
    }

    function onDocumentLoadSuccess({ numPages: loadedPageCount }) {
        setNumPages(loadedPageCount);
        setPageNumber((currentPage) =>
            Math.min(currentPage, loadedPageCount),
        );
    }

    function handleNextPagePress() {
        setPageNumber((currentPage) =>
            Math.min(currentPage + 1, numPages),
        );
    }

    function handlePreviousPagePress() {
        setPageNumber((currentPage) => Math.max(currentPage - 1, 1));
    }

    async function handleEnterPresentationMode() {
        const presentationElement = pdfPresentationRef.current;

        if (
            presentationElement?.requestFullscreen &&
            document.fullscreenEnabled
        ) {
            try {
                await presentationElement.requestFullscreen();
                return;
            } catch (error) {
                // Fall back to an in-page viewport mode below.
            }
        }

        setIsPresentationMode(true);
        setIsPresentationFallback(true);
    }

    async function handleExitPresentationMode() {
        if (document.fullscreenElement === pdfPresentationRef.current) {
            await document.exitFullscreen();
            return;
        }

        setIsPresentationMode(false);
        setIsPresentationFallback(false);
    }

    function handleMenuOpen(event) {
        setAnchorEl(event.currentTarget);
    }

    function handleMenuClose() {
        setAnchorEl(null);
    }

    function handleDownloadClick() {
        handleMenuClose();
        if (file) {
            downloadFile(file);
        }
    }

    function handleCopyLinkClick() {
        navigator.clipboard.writeText(
            `https://${window.location.host}/pdf/${file.key}`,
        );
        handleMenuClose();
    }

    function handleDeleteClick() {
        handleMenuClose();
        setConfirmDeleteDialogIsOpen(true);
    }

    async function handleConfirmDeletePressed() {
        if (!userIsManager || !file) {
            return;
        }

        setDeleteInProgress(true);

        comments.forEach((comment) => {
            if (comment.file === file.key) {
                firebase
                    .database()
                    .ref("comments")
                    .child(comment.key)
                    .update({ deleted: true });
            }
        });

        events.forEach((event) => {
            const filesToKeep = event.files.filter(
                (eventFile) => eventFile.key !== file.key,
            );

            if (filesToKeep.length !== event.files.length) {
                const filesObject = {};
                filesToKeep.forEach((eventFile) => {
                    filesObject[eventFile.key] = true;
                });
                firebase
                    .database()
                    .ref("events")
                    .child(event.key)
                    .update({ files: filesObject });
            }
        });

        users.forEach((user) => {
            const filesToKeep = user.files.filter(
                (userFileKey) => userFileKey !== file.key,
            );

            if (filesToKeep.length !== user.files.length) {
                const filesObject = {};
                filesToKeep.forEach((userFileKey) => {
                    filesObject[userFileKey] = true;
                });
                firebase
                    .database()
                    .ref("users")
                    .child(user.key)
                    .update({ files: filesObject });
            }
        });

        if (!doesFileHaveDuplicate(file, files)) {
            const storageFileKey = file.original || file.key;
            await Promise.allSettled([
                firebase
                    .storage()
                    .ref("files")
                    .child(storageFileKey)
                    .child(file.filename)
                    .delete(),
                firebase
                    .storage()
                    .ref("files")
                    .child(storageFileKey)
                    .child("thumbnail")
                    .delete(),
            ]);
        }

        await deleteFileRecordAndNotifications(file.key);

        setDeleteInProgress(false);
        setConfirmDeleteDialogIsOpen(false);
        handleBackToFiles();
    }

    function handleEditClick() {
        handleMenuClose();
        setEditDialogOpen(true);
    }

    function handleUsageClick() {
        handleMenuClose();
        setUsageDialogOpen(true);
    }

    function handleCloseEditDialog() {
        // EditFileDialog shows its own save confirmation
        setEditDialogOpen(false);
    }

    function handleAddToFavouritesPress() {
        const favouritesObject = {};
        (currentUserData.faveFiles || []).forEach((favouriteFileKey) => {
            favouritesObject[favouriteFileKey] = true;
        });
        favouritesObject[file.key] = true;

        firebase
            .database()
            .ref("users")
            .child(currentUserData.key)
            .update({ faveFiles: favouritesObject });
    }

    function handleRemoveFromFavouritesPress() {
        const favouritesObject = {};
        (currentUserData.faveFiles || []).forEach((favouriteFileKey) => {
            if (favouriteFileKey !== file.key) {
                favouritesObject[favouriteFileKey] = true;
            }
        });

        firebase
            .database()
            .ref("users")
            .child(currentUserData.key)
            .update({ faveFiles: favouritesObject });
    }

    return (
        <>
            <Helmet>
                <meta
                    name="description"
                    content={file ? file.displayName : "Nodiant PDF"}
                />
            </Helmet>

            {userHasPermission === undefined && (
                <Box
                    sx={{
                        minHeight: {
                            xs: "calc(100dvh - 60px)",
                            sm: "calc(100vh - 128px)",
                        },
                        backgroundColor: "background.default",
                        pt: { xs: 2, md: 3 },
                        pb: 10,
                    }}
                >
                    <Container
                        maxWidth={false}
                        sx={{ maxWidth: "1520px", px: { xs: 1.5, sm: 3 } }}
                    >
                        <Skeleton
                            variant="rounded"
                            width={124}
                            height={34}
                            sx={{ mb: 1.5, borderRadius: 2 }}
                        />
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "minmax(0, 1fr)",
                                    md: "minmax(0, 1.8fr) minmax(320px, 0.8fr)",
                                },
                                gap: 2,
                            }}
                        >
                            <Skeleton
                                variant="rounded"
                                width="100%"
                                height={700}
                                sx={{ borderRadius: 3 }}
                            />
                            <Skeleton
                                variant="rounded"
                                width="100%"
                                height={420}
                                sx={{ borderRadius: 3 }}
                            />
                        </Box>
                    </Container>
                </Box>
            )}

            {userHasPermission && (
                <>
                    <Box
                        sx={{
                            minHeight: {
                                xs: "calc(100dvh - 60px)",
                                sm: "calc(100vh - 128px)",
                            },
                            backgroundColor: "background.default",
                            pt: { xs: 1.5, sm: 2.5, md: 3 },
                            pb: 10,
                        }}
                    >
                        <Container
                            maxWidth={false}
                            sx={{
                                maxWidth: "1520px",
                                px: { xs: 1.5, sm: 3 },
                            }}
                        >
                            <Button
                                color="inherit"
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBackToFiles}
                                sx={{ mb: 1.5, color: "text.secondary" }}
                            >
                                Back to Files
                            </Button>

                            {file && (
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "minmax(0, 1fr)",
                                            md: "minmax(0, 1.8fr) minmax(320px, 0.8fr)",
                                        },
                                        gap: 2,
                                        alignItems: "start",
                                    }}
                                >
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            minWidth: 0,
                                            overflow: "hidden",
                                            borderRadius: 3,
                                            borderColor: "divider",
                                            backgroundColor:
                                                "background.paper",
                                            boxShadow:
                                                "0 6px 24px rgba(20, 35, 25, 0.07)",
                                        }}
                                    >
                                        <Box
                                            ref={pdfPresentationRef}
                                            sx={{
                                                position: "relative",
                                                backgroundColor: "background.media",
                                                "&:fullscreen": {
                                                    width: "100%",
                                                    height: "100%",
                                                    overflowY: "auto",
                                                    backgroundColor:
                                                        "background.media",
                                                },
                                                ...(isPresentationFallback && {
                                                    position: "fixed",
                                                    inset: 0,
                                                    zIndex: 1600,
                                                    width: "100vw",
                                                    height: "100dvh",
                                                    overflowY: "auto",
                                                }),
                                            }}
                                        >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                px: { xs: 1.5, sm: 2.5 },
                                                py: 1.5,
                                                color: "text.secondary",
                                                borderBottom: "1px solid",
                                                borderColor: "divider",
                                                backgroundColor:
                                                    "background.paper",
                                                ...(isPresentationMode && {
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 3,
                                                    boxShadow:
                                                        "0 2px 10px rgba(20, 35, 25, 0.1)",
                                                }),
                                            }}
                                        >
                                            <PictureAsPdfOutlinedIcon
                                                sx={{
                                                    color: "primary.main",
                                                }}
                                            />
                                            <Typography
                                                variant="subtitle2"
                                                noWrap
                                                sx={{
                                                    minWidth: 0,
                                                    fontWeight: 800,
                                                    flex: isPresentationMode
                                                        ? 1
                                                        : "initial",
                                                }}
                                            >
                                                {isPresentationMode
                                                    ? file.displayName
                                                    : "PDF document"}
                                            </Typography>
                                            {!isPresentationMode &&
                                                numPages > 0 && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{ ml: "auto" }}
                                                >
                                                    Page {pageNumber} of{" "}
                                                    {numPages}
                                                </Typography>
                                            )}
                                            {isPresentationMode && (
                                                <>
                                                    <IconButton
                                                        aria-label="Previous PDF page"
                                                        onClick={
                                                            handlePreviousPagePress
                                                        }
                                                        disabled={
                                                            pageNumber <= 1
                                                        }
                                                        size="small"
                                                    >
                                                        <KeyboardArrowLeft />
                                                    </IconButton>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            minWidth: {
                                                                xs: 42,
                                                                sm: 86,
                                                            },
                                                            textAlign:
                                                                "center",
                                                        }}
                                                    >
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                display: {
                                                                    xs: "none",
                                                                    sm: "inline",
                                                                },
                                                            }}
                                                        >
                                                            Page{" "}
                                                        </Box>
                                                        {pageNumber}
                                                        {numPages > 0
                                                            ? ` / ${numPages}`
                                                            : ""}
                                                    </Typography>
                                                    <IconButton
                                                        aria-label="Next PDF page"
                                                        onClick={
                                                            handleNextPagePress
                                                        }
                                                        disabled={
                                                            pageNumber >=
                                                            numPages
                                                        }
                                                        size="small"
                                                    >
                                                        <KeyboardArrowRight />
                                                    </IconButton>
                                                    <Tooltip title="Exit full screen">
                                                        <IconButton
                                                            aria-label="Exit PDF full screen"
                                                            onClick={
                                                                handleExitPresentationMode
                                                            }
                                                            size="small"
                                                        >
                                                            <FullscreenExitIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Box>

                                        <Document
                                            file={file.url}
                                            onLoadSuccess={
                                                onDocumentLoadSuccess
                                            }
                                            loading={
                                                <Box
                                                    sx={{
                                                        p: 2,
                                                        backgroundColor:
                                                            "background.media",
                                                    }}
                                                >
                                                    <Skeleton
                                                        variant="rounded"
                                                        width="100%"
                                                        height={680}
                                                    />
                                                </Box>
                                            }
                                            error={
                                                <Box
                                                    sx={{
                                                        minHeight: 320,
                                                        p: 3,
                                                        display: "grid",
                                                        placeItems: "center",
                                                        textAlign: "center",
                                                        backgroundColor:
                                                            "background.media",
                                                    }}
                                                >
                                                    <Typography color="text.secondary">
                                                        This PDF could not be
                                                        displayed.
                                                    </Typography>
                                                </Box>
                                            }
                                        >
                                            <Box
                                                ref={pdfPageHostRef}
                                                sx={{
                                                    width: "100%",
                                                    minHeight: 320,
                                                    p: {
                                                        xs: 1.5,
                                                        sm: 2.5,
                                                    },
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    overflowX: "auto",
                                                    boxSizing: "border-box",
                                                    backgroundColor:
                                                        "background.media",
                                                    "& .react-pdf__Page": {
                                                        boxShadow:
                                                            "0 8px 24px rgba(20, 35, 25, 0.13)",
                                                    },
                                                    "& .react-pdf__Page__canvas":
                                                        {
                                                            maxWidth: "100%",
                                                            height: "auto !important",
                                                        },
                                                }}
                                            >
                                                {pdfWidth && (
                                                    <Page
                                                        pageNumber={pageNumber}
                                                        width={pdfWidth}
                                                    />
                                                )}
                                            </Box>
                                        </Document>

                                        <MobileStepper
                                            steps={Math.max(numPages, 1)}
                                            position="static"
                                            variant="text"
                                            activeStep={pageNumber - 1}
                                            nextButton={
                                                <Button
                                                    size="small"
                                                    onClick={
                                                        handleNextPagePress
                                                    }
                                                    disabled={
                                                        pageNumber >= numPages
                                                    }
                                                >
                                                    Next
                                                    <KeyboardArrowRight />
                                                </Button>
                                            }
                                            backButton={
                                                <Button
                                                    size="small"
                                                    onClick={
                                                        handlePreviousPagePress
                                                    }
                                                    disabled={pageNumber <= 1}
                                                >
                                                    <KeyboardArrowLeft />
                                                    Previous
                                                </Button>
                                            }
                                            sx={{
                                                borderTop: "1px solid",
                                                borderColor: "divider",
                                                backgroundColor:
                                                    "background.paper",
                                            }}
                                        />
                                        </Box>

                                        <CardContent
                                            sx={{
                                                p: { xs: 2, sm: 3 },
                                                "&:last-child": {
                                                    pb: { xs: 2, sm: 3 },
                                                },
                                            }}
                                        >
                                            <Typography
                                                variant="h4"
                                                component="h1"
                                                sx={{
                                                    overflowWrap: "break-word",
                                                    lineHeight: 1.2,
                                                    fontSize: {
                                                        xs: 24,
                                                        sm: 30,
                                                    },
                                                }}
                                            >
                                                {file.displayName}
                                            </Typography>

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    flexWrap: "wrap",
                                                    gap: 1.25,
                                                    mt: 1,
                                                    color: "text.secondary",
                                                }}
                                            >
                                                {folder?.name && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <FolderOutlinedIcon
                                                            sx={{
                                                                fontSize: 17,
                                                            }}
                                                        />
                                                        <Typography variant="body2">
                                                            {folder.name}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {uploadedBy && (
                                                    <Typography variant="body2">
                                                        Uploaded by{" "}
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                color: "text.primary",
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {uploadedBy}
                                                        </Box>
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Divider sx={{ my: 2.5 }} />

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    flexWrap: "wrap",
                                                    gap: 1,
                                                }}
                                            >
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={
                                                        <EventNoteIcon />
                                                    }
                                                    onClick={() =>
                                                        setEventDialogOpen(true)
                                                    }
                                                    sx={{
                                                        flex: {
                                                            xs: "1 1 calc(50% - 4px)",
                                                            sm: "0 0 auto",
                                                        },
                                                    }}
                                                >
                                                    Events
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={
                                                        <FullscreenIcon />
                                                    }
                                                    onClick={
                                                        handleEnterPresentationMode
                                                    }
                                                    sx={{
                                                        flex: {
                                                            xs: "1 1 calc(50% - 4px)",
                                                            sm: "0 0 auto",
                                                        },
                                                    }}
                                                >
                                                    Full screen
                                                </Button>
                                                <Button
                                                    variant={
                                                        isInFavourites
                                                            ? "contained"
                                                            : "outlined"
                                                    }
                                                    size="small"
                                                    startIcon={
                                                        isInFavourites ? (
                                                            <StarIcon />
                                                        ) : (
                                                            <StarBorderIcon />
                                                        )
                                                    }
                                                    onClick={
                                                        isInFavourites
                                                            ? handleRemoveFromFavouritesPress
                                                            : handleAddToFavouritesPress
                                                    }
                                                    sx={{
                                                        flex: {
                                                            xs: "1 1 calc(50% - 4px)",
                                                            sm: "0 0 auto",
                                                        },
                                                    }}
                                                >
                                                    {isInFavourites
                                                        ? "Favourited"
                                                        : "Favourite"}
                                                </Button>
                                                <Tooltip title="More actions">
                                                    <IconButton
                                                        onClick={
                                                            handleMenuOpen
                                                        }
                                                        aria-label="More PDF actions"
                                                        sx={{
                                                            ml: "auto",
                                                            border: "1px solid",
                                                            borderColor: "divider",
                                                            borderRadius: 2,
                                                        }}
                                                    >
                                                        <MoreHorizIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </CardContent>
                                    </Card>

                                    <Card
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 3,
                                            borderColor: "divider",
                                            backgroundColor:
                                                "background.paper",
                                            boxShadow:
                                                "0 2px 10px rgba(20, 35, 25, 0.045)",
                                            position: { md: "sticky" },
                                            top: { md: 80 },
                                            overflow: "hidden",
                                        }}
                                    >
                                        {file.allowComments ? (
                                            <CommentSection
                                                file={file}
                                                modern
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    minHeight: 260,
                                                    p: 3,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    textAlign: "center",
                                                }}
                                            >
                                                <ForumOutlinedIcon
                                                    sx={{
                                                        fontSize: 38,
                                                        mb: 1.25,
                                                        color: "text.disabled",
                                                    }}
                                                />
                                                <Typography
                                                    variant="h6"
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    Comments unavailable
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Comments are disabled for
                                                    this file.
                                                </Typography>
                                            </Box>
                                        )}
                                    </Card>
                                </Box>
                            )}
                        </Container>
                    </Box>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                    >
                        <MenuItem onClick={handleCopyLinkClick}>
                            <ListItemIcon>
                                <ContentCopyOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            Copy Link
                        </MenuItem>
                        {(userIsManager || file.allowSharing) && (
                            <MenuItem
                                onClick={() => {
                                    handleMenuClose();
                                    setShareLinkDialogOpen(true);
                                }}
                            >
                                <ListItemIcon>
                                    <PublicOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                Share Public Link
                            </MenuItem>
                        )}
                        {userIsManager && (
                            <MenuItem onClick={handleEditClick}>
                                <ListItemIcon>
                                    <EditOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                Edit File
                            </MenuItem>
                        )}
                        {userIsManager && (
                            <MenuItem onClick={handleUsageClick}>
                                <ListItemIcon>
                                    <DataUsageOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                File Usage Data
                            </MenuItem>
                        )}
                        <MenuItem
                            disabled={!file.allowDownloads}
                            onClick={handleDownloadClick}
                        >
                            <ListItemIcon>
                                <DownloadOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            Download File
                        </MenuItem>
                        {userIsManager && <Divider />}
                        {userIsManager && (
                            <MenuItem
                                onClick={handleDeleteClick}
                                sx={{ color: "error.main" }}
                            >
                                <ListItemIcon sx={{ color: "error.main" }}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </ListItemIcon>
                                Delete File
                            </MenuItem>
                        )}
                    </Menu>
                </>
            )}

            {confirmDeleteDialogIsOpen && (
                <DeleteFileDialog
                    open={confirmDeleteDialogIsOpen}
                    onClose={() => setConfirmDeleteDialogIsOpen(false)}
                    onConfirm={handleConfirmDeletePressed}
                    fileName={file?.displayName}
                    isDeleting={deleteInProgress}
                />
            )}
            {userHasPermission === false && <NoPermissionsMessage />}

            <ToolbarWithLogo />

            {eventDialogOpen && (
                <FileEventsDialog
                    open={eventDialogOpen}
                    onClose={() => setEventDialogOpen(false)}
                    file={file}
                />
            )}
            {editDialogOpen && (
                <EditFileDialog
                    open={editDialogOpen}
                    onClose={handleCloseEditDialog}
                    file={file}
                    folder={folder}
                />
            )}
            {usageDialogOpen && (
                <FileUsageDialog
                    open={usageDialogOpen}
                    onClose={() => setUsageDialogOpen(false)}
                    file={file}
                />
            )}
            {shareLinkDialogOpen && (
                <ShareLinkDialog
                    open={shareLinkDialogOpen}
                    onClose={() => setShareLinkDialogOpen(false)}
                    file={file}
                />
            )}
        </>
    );
}
